import { createFileRoute, Link } from "@tanstack/react-router";
import * as m from "~/paraglide/messages";
import { JsonLd } from "~/components/seo/JsonLd";
import { breadcrumbSchema, webPageSchema, schemaGraph } from "~/lib/seo/schema";

export const Route = createFileRoute("/agb")({
  component: AgbPage,
});

function AgbPage() {
  const jsonLd = schemaGraph(
    webPageSchema({
      name: "AGB — Alchema",
      description:
        "General terms and conditions (AGB) for Alchema. Scope, registration, subscriptions, liability, and governing law.",
      url: "/agb",
    }),
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "AGB", url: "/agb" },
    ]),
  );

  return (
    <div
      className="mx-auto max-w-3xl px-6 py-16"
      style={{ color: "var(--text)" }}
    >
      <JsonLd json={jsonLd} />
      <h1
        className="text-3xl font-bold tracking-tight"
        style={{ color: "var(--text)" }}
      >
        {m.agb_title()}
      </h1>
      <p
        className="mt-2 text-sm"
        style={{ color: "var(--text-muted)" }}
      >
        {m.agb_last_updated()}
      </p>

      {/* 1. Scope */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.agb_scope_title()}
        </h2>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.agb_scope_text()}
        </p>
      </section>

      {/* 2. Subject */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.agb_subject_title()}
        </h2>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.agb_subject_text()}
        </p>
      </section>

      {/* 3. Registration */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.agb_registration_title()}
        </h2>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.agb_registration_text()}
        </p>
      </section>

      {/* 4. Services */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.agb_services_title()}
        </h2>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.agb_services_text()}
        </p>
        <ul
          className="mt-3 list-disc space-y-1.5 pl-5 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          <li>
            <strong>{m.agb_tier_free_label()}</strong>
            {m.agb_tier_free_desc()}
          </li>
          <li>
            <strong>{m.agb_tier_student_label()}</strong>
            {m.agb_tier_student_desc()}
          </li>
          <li>
            <strong>{m.agb_tier_pro_label()}</strong>
            {m.agb_tier_pro_desc()}
          </li>
        </ul>
      </section>

      {/* 5. Pricing */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.agb_pricing_title()}
        </h2>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.agb_pricing_text()}
        </p>
      </section>

      {/* 6. Credits & AI */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.agb_credits_title()}
        </h2>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.agb_credits_text()}
        </p>
      </section>

      {/* 6a. Credit Packs */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.agb_credit_packs_title()}
        </h2>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.agb_credit_packs_intro()}
        </p>
        <ul
          className="mt-3 list-disc space-y-1.5 pl-5 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          <li>{m.agb_credit_packs_starter()}</li>
          <li>{m.agb_credit_packs_boost()}</li>
          <li>{m.agb_credit_packs_power()}</li>
        </ul>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.agb_credit_packs_no_expiry()}
        </p>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.agb_credit_packs_fefo()}
        </p>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.agb_credit_packs_non_refundable()}
        </p>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.agb_credit_packs_waiver()}
        </p>
      </section>

      {/* 6b. Dispute Resolution (VSBG S36) */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.agb_dispute_resolution_title()}
        </h2>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.agb_dispute_resolution_text()}
        </p>
      </section>

      {/* 7. Right of Withdrawal */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.agb_withdrawal_title()}
        </h2>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.agb_withdrawal_text()}
        </p>
        <Link
          to="/widerruf"
          className="btn btn-secondary mt-4 inline-flex"
        >
          {m.consumer_declaration_withdraw_first()}
        </Link>
      </section>

      {/* 8. Liability */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.agb_liability_title()}
        </h2>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.agb_liability_text()}
        </p>
      </section>

      {/* 9. Data Protection */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.agb_data_protection_title()}
        </h2>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.agb_data_protection_text()}{" "}
          <Link
            to="/privacy"
            className="underline underline-offset-2"
            style={{ color: "var(--accent)" }}
          >
            {m.agb_data_protection_link()}
          </Link>
          .
        </p>
      </section>

      {/* 10. Cancellation */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.agb_cancellation_title()}
        </h2>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.agb_cancellation_text()}
        </p>
        <Link
          to="/kuendigung"
          className="btn btn-secondary mt-4 inline-flex"
        >
          {m.consumer_declaration_termination_first()}
        </Link>
      </section>

      {/* 11. Changes */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.agb_changes_title()}
        </h2>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.agb_changes_text()}
        </p>
      </section>

      {/* 12. Final Provisions */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.agb_final_title()}
        </h2>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {m.agb_final_text()}
        </p>
      </section>

      {/* Back to home */}
      <div className="mt-16">
        <Link
          to="/"
          className="text-sm underline underline-offset-2"
          style={{ color: "var(--accent)" }}
        >
          {m.impressum_back_home()}
        </Link>
      </div>
    </div>
  );
}
